import { Injectable } from '@angular/core';
import * as faceapi from '@vladmandic/face-api';
import { FonteAnaliseCameraFacial, ServicoCameraFacial } from './servico-camera-facial';

export interface ResultadoAnaliseFacial {
  rostoDetectado: boolean;
  provaVidaOk: boolean;
  descritorFacial: number[];
  caixaFace?: CaixaFaceDetectada;
  mensagem: string;
}

export interface CaixaFaceDetectada {
  esquerda: number;
  topo: number;
  largura: number;
  altura: number;
}

@Injectable({ providedIn: 'root' })
export class ServicoReconhecimentoFacial {
  private carregandoDetector?: Promise<boolean>;
  private olhosAbertosDetectados = false;
  private maiorAberturaOlhos = 0;
  private menorAberturaOlhos = Number.POSITIVE_INFINITY;
  private amostrasOlhos: number[] = [];
  private provaVidaOk = false;

  constructor(private readonly servicoCameraFacial: ServicoCameraFacial) {}

  async carregarDetector(): Promise<boolean> {
    if (faceapi.nets.tinyFaceDetector.isLoaded && faceapi.nets.faceLandmark68Net.isLoaded && faceapi.nets.faceRecognitionNet.isLoaded) {
      return true;
    }

    if (!this.carregandoDetector) {
      this.carregandoDetector = Promise
        .resolve()
        .then(async () => {
          const tf = (faceapi as unknown as { tf?: { setBackend(name: string): Promise<boolean>; ready(): Promise<void> } }).tf;
          if (tf) {
            const webglOk = await tf.setBackend('webgl').catch(() => false);
            if (!webglOk) {
              await tf.setBackend('cpu');
            }
            await tf.ready();
          }
        })
        .then(() => Promise.all([
          faceapi.nets.tinyFaceDetector.loadFromUri('/assets/face-api'),
          faceapi.nets.faceLandmark68Net.loadFromUri('/assets/face-api'),
          faceapi.nets.faceRecognitionNet.loadFromUri('/assets/face-api')
        ]))
        .then(() => true)
        .catch(() => false);
    }

    return this.carregandoDetector;
  }

  async analisarComTempoLimite(
    video: HTMLVideoElement,
    cameraMobile: boolean,
    limiteMs = 2500
  ): Promise<ResultadoAnaliseFacial> {
    return Promise.race([
      this.analisar(video, cameraMobile),
      new Promise<ResultadoAnaliseFacial>((_, reject) => {
        window.setTimeout(() => reject(new Error('ANALISE_FACIAL_TIMEOUT')), limiteMs);
      })
    ]);
  }

  async analisar(video: HTMLVideoElement, cameraMobile: boolean): Promise<ResultadoAnaliseFacial> {
    const fonteAnalise = this.servicoCameraFacial.prepararFonteAnalise(video);
    const resultados = await faceapi
      .detectAllFaces(fonteAnalise.fonte, new faceapi.TinyFaceDetectorOptions({
        inputSize: cameraMobile ? 416 : 320,
        scoreThreshold: cameraMobile ? 0.35 : 0.45
      }))
      .withFaceLandmarks()
      .withFaceDescriptors();

    if (resultados.length === 0) {
      return {
        rostoDetectado: false,
        provaVidaOk: this.provaVidaOk,
        descritorFacial: [],
        caixaFace: undefined,
        mensagem: 'Nenhum rosto detectado.'
      };
    }

    if (resultados.length > 1) {
      this.resetarProvaVida();
      return {
        rostoDetectado: false,
        provaVidaOk: false,
        descritorFacial: [],
        caixaFace: undefined,
        mensagem: 'Mais de um rosto detectado. Permita apenas uma pessoa na camera.'
      };
    }

    const resultado = resultados[0];
    const face = this.converterFaceParaVideoOriginal(resultado.detection.box, fonteAnalise);
    const videoWidth = video.videoWidth || video.clientWidth;
    const videoHeight = video.videoHeight || video.clientHeight;
    const faceCenterX = face.x + face.width / 2;
    const faceCenterY = face.y + face.height / 2;
    const centeredToleranceX = cameraMobile ? 0.24 : 0.18;
    const centeredToleranceY = cameraMobile ? 0.28 : 0.22;
    const minFaceWidth = cameraMobile ? 0.11 : 0.16;
    const maxFaceWidth = cameraMobile ? 0.72 : 0.48;
    const minFaceHeight = cameraMobile ? 0.16 : 0.22;
    const maxFaceHeight = cameraMobile ? 0.88 : 0.78;
    const centralizado = Math.abs(faceCenterX - videoWidth / 2) < videoWidth * centeredToleranceX
      && Math.abs(faceCenterY - videoHeight * 0.48) < videoHeight * centeredToleranceY;
    const tamanhoAdequado = face.width > videoWidth * minFaceWidth
      && face.width < videoWidth * maxFaceWidth
      && face.height > videoHeight * minFaceHeight
      && face.height < videoHeight * maxFaceHeight;

    const rostoDetectado = centralizado && tamanhoAdequado;
    const caixaFace = this.mapearCaixaFaceParaVideoExibido(face, video, videoWidth, videoHeight);
    this.atualizarProvaDeVida(resultado.landmarks);
    const mensagem = rostoDetectado && this.provaVidaOk
      ? 'Rosto detectado, enquadrado e piscada confirmada.'
      : rostoDetectado
        ? 'Rosto enquadrado. Pisque uma vez de forma natural.'
        : 'Aproxime e centralize o rosto na moldura.';

    return {
      rostoDetectado,
      provaVidaOk: this.provaVidaOk,
      descritorFacial: Array.from(resultado.descriptor),
      caixaFace,
      mensagem
    };
  }

  resetarProvaVida() {
    this.olhosAbertosDetectados = false;
    this.maiorAberturaOlhos = 0;
    this.menorAberturaOlhos = Number.POSITIVE_INFINITY;
    this.amostrasOlhos = [];
    this.provaVidaOk = false;
  }

  private atualizarProvaDeVida(landmarks: faceapi.FaceLandmarks68) {
    const leftEar = this.proporcaoAberturaOlho(landmarks.getLeftEye());
    const rightEar = this.proporcaoAberturaOlho(landmarks.getRightEye());
    const ear = (leftEar + rightEar) / 2;

    this.amostrasOlhos.push(ear);
    if (this.amostrasOlhos.length > 18) {
      this.amostrasOlhos.shift();
    }

    this.maiorAberturaOlhos = Math.max(this.maiorAberturaOlhos, ear);
    this.menorAberturaOlhos = Math.min(this.menorAberturaOlhos, ear);

    if (ear > 0.10) {
      this.olhosAbertosDetectados = true;
    }

    const maxJanela = Math.max(...this.amostrasOlhos);
    const minJanela = Math.min(...this.amostrasOlhos);
    const quedaRelativa = maxJanela > 0 ? (maxJanela - minJanela) / maxJanela : 0;
    const piscadaPorQuedaAtual = this.olhosAbertosDetectados && this.maiorAberturaOlhos > 0 && ear < this.maiorAberturaOlhos * 0.88;
    const piscadaPorJanela = this.amostrasOlhos.length >= 5 && quedaRelativa > 0.12;

    if (piscadaPorQuedaAtual || piscadaPorJanela) {
      this.provaVidaOk = true;
    }
  }

  private proporcaoAberturaOlho(points: faceapi.Point[]) {
    const vertical1 = this.distancia(points[1], points[5]);
    const vertical2 = this.distancia(points[2], points[4]);
    const horizontal = this.distancia(points[0], points[3]);
    return (vertical1 + vertical2) / (2 * horizontal);
  }

  private distancia(a: faceapi.Point, b: faceapi.Point) {
    return Math.hypot(a.x - b.x, a.y - b.y);
  }

  private converterFaceParaVideoOriginal(face: faceapi.Box, fonteAnalise: FonteAnaliseCameraFacial): faceapi.Box {
    if (fonteAnalise.fonte instanceof HTMLVideoElement) {
      return face;
    }

    return new faceapi.Box({
      x: fonteAnalise.deslocamentoX + face.x * fonteAnalise.escalaX,
      y: fonteAnalise.deslocamentoY + face.y * fonteAnalise.escalaY,
      width: face.width * fonteAnalise.escalaX,
      height: face.height * fonteAnalise.escalaY
    });
  }

  private mapearCaixaFaceParaVideoExibido(
    face: faceapi.Box,
    video: HTMLVideoElement,
    videoWidth: number,
    videoHeight: number
  ): CaixaFaceDetectada {
    const larguraElemento = video.clientWidth || videoWidth;
    const alturaElemento = video.clientHeight || videoHeight;
    const escala = Math.max(larguraElemento / videoWidth, alturaElemento / videoHeight);
    const larguraRenderizada = videoWidth * escala;
    const alturaRenderizada = videoHeight * escala;
    const deslocamentoX = (larguraElemento - larguraRenderizada) / 2;
    const deslocamentoY = (alturaElemento - alturaRenderizada) / 2;
    const margemHorizontal = face.width * 0.12;
    const margemVertical = face.height * 0.16;

    const esquerda = ((face.x - margemHorizontal) * escala + deslocamentoX) / larguraElemento * 100;
    const topo = ((face.y - margemVertical) * escala + deslocamentoY) / alturaElemento * 100;
    const largura = ((face.width + margemHorizontal * 2) * escala) / larguraElemento * 100;
    const altura = ((face.height + margemVertical * 2) * escala) / alturaElemento * 100;

    return {
      esquerda: this.limitarPercentual(esquerda),
      topo: this.limitarPercentual(topo),
      largura: this.limitarPercentual(largura),
      altura: this.limitarPercentual(altura)
    };
  }

  private limitarPercentual(valor: number) {
    return Math.max(0, Math.min(100, valor));
  }
}
